import {Controller, useWatch} from "react-hook-form";
import {memo, useEffect, useMemo, useState} from "react";
import {FormHelperText, Grid, InputLabel, TextField, Typography} from "@mui/material";
import clsx from "clsx";
import {useStyles} from "../../utils/uiUtils";
import FormControl from "@mui/material/FormControl";
import Select from '@mui/material/Select';
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import {isPlainDict, SIZE_MULTIPLIER} from "../../utils/utils";
import {numberValidationRules, sizeUnitValidationRules} from "../../utils/validationRules";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Switch from "@mui/material/Switch";
import Box from "@mui/material/Box";
import {applyNationalInput, DIAL_CODES, joinPhoneNumber, splitPhoneNumber} from "../../utils/phoneNumbers";


/**
 * Component that renders a standard data field used in Create Edit dialog boxes
 * @param props
 * @param {CreateEditFieldConfig} props.fieldConfig - The configuration that will determine how this field will be rendered.
 * @param {Object} props.processedData - The data of the entry that was pre-processed for easier access and normalization.
 * @param props.defaultValue - The default value of the field
 * @param props.register - React form register method
 * @param props.control - React form control method
 * @param props.getValues - React form getValues method
 * @param props.trigger - React form trigger method
 * @param props.errors - React form errors container
 * @constructor
 */
export const DataField = memo((props) => {
    const {
        data_testid,
        fieldConfig,
        processedData,
        defaultValue,
        register,
        control,
        getValues,
        trigger,
        errors
    } = props;

    const classes = useStyles();
    /*
     * The country code an operator picked before typing any digits. Only that case needs remembering: once
     * there are digits the stored string carries its own code and the select reads it back from there, which
     * is what keeps the two boxes right when the dialog loads a record it fetched after the first render.
     */
    const [pickedDialCode, setPickedDialCode] = useState(undefined);
    const [isMultiSelectField, setIsMultiSelectField] = useState(false);
    const [isSingleOption, setIsSingleOption] = useState(true);
    const [optionValidatorDependencyFieldName, setOptionValidatorDependencyFieldName] = useState(undefined);

    const dependencyName = ['multiselect'].includes(fieldConfig.type) ? fieldConfig.options?.optionValidatorDependencyFieldName : undefined;

    /**
     * Watch the value of the field that the optionValidator depends on, if any.
     * This is needed so that when the dependency field changes, this field re-renders and re-evaluates its options.
     */
    const dependencyValue = useWatch({
        control,
        name: dependencyName || "",
        defaultValue: undefined,
    });

    // Memoize the filtered menu item list at component level to avoid rebuilding large option arrays (e.g. country list) on every render
    const currentFieldOptionsSource = fieldConfig.options?.source;
    const filteredMenuItemList = useMemo(() => {
        // Getting the entries for each MenuItem either:
        // 1. from processedData
        //    where processedData[key] and fieldConfig -> options -> source = key
        // 2. either directly from fieldConfig -> options -> source
        //    where fieldConfig -> options -> source is a list of objects
        let list = [];
        if (typeof currentFieldOptionsSource === 'string') list = [...(processedData?.[currentFieldOptionsSource] ?? [])];
        else if (Array.isArray(currentFieldOptionsSource)) list = currentFieldOptionsSource;

        // --------------------- Validation of each menu item  ---------------------
        // This might occur if for example current field is a file type filter and another filter like file type group,
        // already filtered only database type files. So this one, should include only database type files.
        if (fieldConfig.options?.optionValidator) {
            const validatorFn = fieldConfig.options.optionValidator;
            return list.filter(optionValue => validatorFn(optionValue, fieldConfig.options.valueKey ?? undefined, dependencyValue, processedData));
        }
        return list;
    }, [currentFieldOptionsSource, processedData, fieldConfig, dependencyValue]);

    useEffect(() => {
        if (['multiselect'].includes(fieldConfig.type)) {
            setIsMultiSelectField(true);
            setIsSingleOption(fieldConfig.options?.isSingleOption ?? true);

            const fieldToBeUsedForValidation = fieldConfig.options?.optionValidatorDependencyFieldName || undefined;
            setOptionValidatorDependencyFieldName(fieldToBeUsedForValidation);

            // console.log("DataField -> useEffect -> optionValidatorDependencyFieldName:",
            //     {
            //         field: fieldConfig.valueProperty,
            //         optionValidatorDependencyFieldName,
            //         fieldConfig,
            //         fieldToBeUsedForValidation
            //     });

        } else setIsMultiSelectField(false);
    }, [fieldConfig]);

    const {localConfigFieldProps, localConfigRules} = useMemo(() => {
        const cfg = {
            toggleInput: {},
            text: {
                localConfigFieldProps: {
                    type: 'text',
                    inputProps: {maxLength: 300, inputMode: 'text'},
                },
                localConfigRules: {
                    maxLength: {value: 300, message: 'Maximum 300 characters'},
                },
            },
            numberInput: {
                localConfigFieldProps: {
                    type: 'number',
                    inputProps: {step: 1, pattern: '[0-9]*', inputMode: 'numeric'},
                },
                localConfigRules: {
                    valueAsNumber: true,
                    pattern: {value: /^\d+$/, message: 'Whole numbers only'},
                },
            },
            sizeInput: {
                localConfigFieldProps: {
                    type: 'number',
                    inputProps: {step: 0.01, min: 0, max: 99999999.99, inputMode: 'decimal'},
                },
                localConfigRules: {
                    valueAsNumber: true,
                    pattern: {
                        value: /^\d+([.,]\d+)?$/,
                        message: 'Decimal number (e.g. 123.45)',
                    },
                },
            },
            dateInput: {
                localConfigFieldProps: {
                    type: 'date',
                    InputLabelProps: {shrink: true},
                },
                localConfigRules: {},          // add any custom date checks here
            },
            multiselect: {
                localConfigFieldProps: {type: undefined}
            }
            // multiselect will normally use an <Autocomplete multiple> or <Select multiple>,
            // so it isn’t handled by TextField directly.
        }
        return cfg[fieldConfig.type] ?? {localConfigFieldProps: {}, localConfigRules: {}};
    }, [fieldConfig]);

    const errorColorFix = {
        FormHelperTextProps: {
            sx: {fontWeight: 500}, // weight only, color overridden below
        },
        sx: {
            // helper text (error message)
            "& .MuiFormHelperText-root.Mui-error": {
                color: "#FFC540",
                fontWeight: 500,
            },
            // outline/border when in error
            "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FFC540",
            },
            // label when in error
            "& .MuiFormLabel-root.Mui-error": {
                color: "#FFC540",
            },
        },
    };

    const fieldName = fieldConfig.valueProperty.replace(/\s+/g, "_").toLowerCase();
    let rules = {
        ...localConfigRules,
        ...(fieldConfig.options?.validationRules ?? {})
    };
    const isRequired = !!rules.required;
    const xs = fieldConfig.options?.xs ?? 12;

    const renderTextField = () => {
        if (!['text'].includes(fieldConfig.type)) return <></>;

        return (
            <Grid item xs={xs} data-testid={data_testid ?? undefined}>
                <Controller
                    name={fieldName}
                    control={control}
                    defaultValue={defaultValue}
                    rules={rules}
                    render={({field, fieldState}) => (
                        <TextField
                            {...field}
                            {...localConfigFieldProps}
                            required={isRequired}
                            {...errorColorFix}
                            className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                            label={fieldConfig?.label || 'Unknown Field Label'}
                            error={!!fieldState.error}
                            fullWidth
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
            </Grid>);
    }

    /**
     * A phone number as two boxes over one stored string: a country code picked from a list, and the national
     * number typed beside it. Same treatment the Account step's Primary Contact already had — asked for as
     * "the country code and mobile number should be there same as we did previously".
     *
     * The form value stays the single joined string the API stores ("+61 400 111 222"), so nothing downstream
     * of this field changes shape. What the two boxes buy is a format check worth having: while the code was
     * typed into the same box, the pattern had to recognise it, which in practice meant only +61 and +1 were
     * accepted and every other country was a validation error however correctly it was written.
     */
    const renderDialledNumberField = () => {
        if (!['dialledNumberInput'].includes(fieldConfig.type)) return <></>;

        /*
         * The configured pattern describes the national number, but the value held by the form is the joined
         * string, so a pattern rule would test it with the country code still attached and reject everything.
         * Moved to a validate entry that checks the national half. required is left alone: it fires on the
         * empty string, and joinPhoneNumber returns exactly that when the number box is empty, code or no code.
         */
        const {pattern, validate, ...restOfRules} = rules;
        const dialledRules = {
            ...restOfRules,
            validate: {
                ...(isPlainDict(validate) ? validate : (validate ? {configured: validate} : {})),
                ...(pattern ? {
                    nationalNumberFormat: (value) => {
                        const national = splitPhoneNumber(value).national;
                        return national === '' || pattern.value.test(national) || pattern.message;
                    },
                } : {}),
            },
        };

        return (
            <Grid item xs={xs} data-testid={data_testid ?? undefined}>
                <Controller
                    name={fieldName}
                    control={control}
                    defaultValue={defaultValue}
                    rules={dialledRules}
                    render={({field, fieldState}) => {
                        const stored = splitPhoneNumber(field.value);
                        // A stored number states its own code; the picked one only stands in while there is
                        // nothing typed for it to be read from.
                        const dial = stored.national === '' ? (pickedDialCode ?? stored.dial) : stored.dial;

                        const commit = (nextDial, national) => {
                            setPickedDialCode(nextDial);
                            field.onChange(joinPhoneNumber(nextDial, national));
                            trigger?.(fieldName);
                        };

                        return (
                            <Box sx={{display: 'flex', gap: 1}}>
                                <TextField
                                    // "Country code", not "Phone Number code"/"Mobile Number code": what the box
                                    // holds is the country, and the field it belongs to is already named by the
                                    // box beside it.
                                    label="Country code"
                                    // Its own testid because the label is not reachable from a test: this field
                                    // is rendered inside a disablePortal dialog, whose aria-hidden wrapper makes
                                    // every accessible name in it compute as empty.
                                    data-testid={data_testid ? `${data_testid}-code` : undefined}
                                    select
                                    value={dial}
                                    onChange={(e) => commit(e.target.value, stored.national)}
                                    className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                    InputLabelProps={{shrink: true}}
                                    // Closed, the box shows just "+61" — the country name is only useful while
                                    // choosing, and would not fit here anyway.
                                    SelectProps={{renderValue: (selected) => selected}}
                                    sx={{flex: '0 0 auto', width: 124}}
                                >
                                    {/* Country first, matching UserBadge: the list is sorted by country and
                                      * long enough that a leading code reads as unsorted. */}
                                    {DIAL_CODES.map(({dial: dialOption, label: country}) => (
                                        <MenuItem key={dialOption} value={dialOption}>{`${country}  ${dialOption}`}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    {...field}
                                    inputProps={{maxLength: 300, inputMode: 'tel'}}
                                    required={isRequired}
                                    {...errorColorFix}
                                    className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                    label={fieldConfig?.label || 'Unknown Field Label'}
                                    // The box holds the national half; the code lives in the select beside it.
                                    value={stored.national}
                                    // A whole international number pasted in here ("+65 9123 4567" out of an
                                    // email) moves its code into the select instead of being kept as digits.
                                    onChange={(e) => {
                                        const applied = applyNationalInput(e.target.value, dial);
                                        commit(applied.dial, applied.national);
                                    }}
                                    error={!!fieldState.error}
                                    fullWidth
                                    helperText={fieldState.error?.message}
                                />
                            </Box>
                        );
                    }}
                />
            </Grid>);
    };

    const renderMultiSelectField = () => {
        if (!isMultiSelectField) return <></>;


        const formControlErrorColorFix = {
            // helper text (error message)
            "& .MuiFormHelperText-root.Mui-error": {
                color: "#FFC540",
                fontWeight: 500,
            },
            // outline/border when in error
            "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FFC540",
            },
            // label when in error
            "& .MuiFormLabel-root.Mui-error": {
                color: "#FFC540",
            },
        };

        const coerceValueToSingleOrMultipleOptionsForm = (value) => {
            return !isSingleOption
                ? (Array.isArray(value) ? value : [value].filter(v => v !== undefined))
                : (Array.isArray(value) ? (value[0] !== undefined ? value[0] : "") : (value !== undefined ? value : ""));
        };


        return (
            <Grid item xs={xs} data-testid={data_testid ?? undefined}>
                <Controller
                    name={fieldName}
                    control={control}
                    defaultValue={coerceValueToSingleOrMultipleOptionsForm(defaultValue)}
                    rules={rules}
                    render={({field, fieldState}) => {
                        const coercedValue = coerceValueToSingleOrMultipleOptionsForm(field.value);

                        const handleChange = (event) => {
                            const {value} = event.target;
                            field.onChange(coerceValueToSingleOrMultipleOptionsForm(value));
                            trigger?.(fieldName);
                        };

                        return (
                            <FormControl
                                fullWidth
                                className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                error={!!fieldState.error}
                                sx={formControlErrorColorFix}
                            >
                                <InputLabel id={`${fieldName}-label`} required={isRequired}>
                                    {fieldConfig?.label || "Unknown Filter Label"}
                                </InputLabel>

                                <Select
                                    {...field} // RHF wiring
                                    labelId={`${fieldName}-label`}
                                    multiple={!isSingleOption}
                                    value={coercedValue}
                                    onChange={handleChange}
                                    input={
                                        <OutlinedInput
                                            label={fieldConfig?.label || "Unknown Filter Label"}
                                            {...localConfigFieldProps}
                                        />
                                    }
                                >
                                    {filteredMenuItemList.map((option, idx) => [
                                            <MenuItem
                                                key={`${fieldName}_item_${idx}`}
                                                value={isPlainDict(option) ? option[fieldConfig.options?.valueKey] : option}
                                            >
                                                {isPlainDict(option) ? option[fieldConfig.options?.displayKey] : option}
                                            </MenuItem>
                                        ]
                                    )}
                                </Select>

                                {fieldState.error?.message && (
                                    <FormHelperText>
                                        {fieldState.error?.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        );
                    }}
                />
            </Grid>);
    };

    const renderSizeInputField = () => {
        if (!['sizeInput'].includes(fieldConfig.type)) return <></>;

        const decimals = fieldConfig.options?.decimals ?? 0;

        const updatedRules = {
            ...rules,
            ...numberValidationRules(decimals)
        };

        const defaultSizeUnitValue = fieldConfig.options?.unit && Object.keys(SIZE_MULTIPLIER).includes(fieldConfig.options?.unit) ? fieldConfig.options?.unit : 'B';

        const slotProps = {
            input: {
                inputMode: decimals === 0 ? "numeric" : "decimal", // keyboard hint
                step: decimals === 0 ? 1 : Math.pow(10, -decimals), // 1 for int, 0.1/0.01/... for floats
                min: 0,
                max: decimals === 0 ? 99999999 : 99999999 + (1 - Math.pow(10, -decimals)),
                pattern: updatedRules.pattern.value,
            },
        };

        return (
            <Grid item xs={xs} data-testid={data_testid ?? undefined}>
                <Grid container spacing={2}>
                    <Grid item xs={8}>
                        <Controller
                            name={fieldName}
                            control={control}
                            defaultValue={defaultValue}
                            rules={updatedRules}
                            render={({field, fieldState}) => (
                                <TextField
                                    {...field}
                                    type="number"
                                    required={isRequired}
                                    slotProps={slotProps}
                                    inputProps={slotProps.input}
                                    {...errorColorFix}
                                    className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                    label={fieldConfig?.label || 'Unknown Field Label'}
                                    error={!!fieldState.error}
                                    fullWidth
                                    helperText={fieldState.error?.message}
                                    onChange={(e) => {
                                        field.onChange(e);
                                        trigger([fieldName]);
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <Controller
                            name={`${fieldName}__size_unit`}
                            control={control}
                            defaultValue={defaultSizeUnitValue}
                            rules={sizeUnitValidationRules}
                            render={({field, fieldState}) => (
                                <TextField
                                    {...field}
                                    className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                    fullWidth
                                    select
                                    onChange={(e) => {
                                        field.onChange(e);
                                        trigger([`${fieldName}__size_unit`]);
                                    }}
                                >
                                    {Object.keys(SIZE_MULTIPLIER).map((sizeUnit, index) => [
                                        <MenuItem
                                            key={`${fieldName}__size_unit_${index}`}
                                            value={sizeUnit}
                                        >
                                            {sizeUnit}
                                        </MenuItem>
                                    ])}
                                </TextField>
                            )}
                        />
                    </Grid>
                </Grid>
            </Grid>);
    };

    const renderNumberInputField = () => {
        if (!['numberInput'].includes(fieldConfig.type)) return <></>;

        const decimals = fieldConfig.options?.decimals ?? 0;

        const updatedRules = {
            ...rules,
            ...numberValidationRules(decimals)
        };

        const slotProps = {
            input: {
                inputMode: decimals === 0 ? "numeric" : "decimal", // keyboard hint
                step: decimals === 0 ? 1 : Math.pow(10, -decimals), // 1 for int, 0.1/0.01/... for floats
                min: 0,
                max: decimals === 0 ? 99999999 : 99999999 + (1 - Math.pow(10, -decimals)),
                pattern: updatedRules.pattern.value,
            },
        };

        return (
            <Grid item xs={xs} data-testid={data_testid ?? undefined}>
                <Controller
                    name={fieldName}
                    control={control}
                    defaultValue={defaultValue}
                    rules={updatedRules}
                    render={({field, fieldState}) => (
                        <TextField
                            {...field}
                            type="number"
                            required={isRequired}
                            slotProps={slotProps}
                            inputProps={slotProps.input}
                            {...errorColorFix}
                            className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                            label={fieldConfig?.label || 'Unknown Field Label'}
                            error={!!fieldState.error}
                            fullWidth
                            helperText={fieldState.error?.message}
                            onChange={(e) => {
                                field.onChange(e);
                                trigger([fieldName]);
                            }}
                        />
                    )}
                />
            </Grid>
        );
    };

    const renderDateInputField = () => {
        if (!['dateInput'].includes(fieldConfig.type)) return <></>;

        return (
            <>
                <Grid item xs={xs} data-testid={data_testid ?? undefined}>
                    <Controller
                        name={fieldName}
                        control={control}
                        defaultValue={defaultValue}
                        rules={rules}
                        render={({field, fieldState}) => (
                            <TextField
                                {...field}
                                {...localConfigFieldProps}
                                required={isRequired}
                                {...errorColorFix}
                                className={clsx(classes.selectLabel, classes.selectLabelGreyDiff)}
                                label={fieldConfig?.label || 'Unknown Field Label'}
                                error={!!fieldState.error}
                                fullWidth
                                helperText={fieldState.error?.message}
                                onChange={(e) => {
                                    field.onChange(e);
                                    trigger([fieldName]);
                                }}
                            />
                        )}
                    />
                </Grid>
            </>);
    };

    const renderToggleInputField = () => {
        if (!['toggleInput'].includes(fieldConfig.type)) return <></>;

        return (
            <Grid item xs={xs} data-testid={data_testid ?? undefined} alignItems="center" justifyContent="center" display="flex">
                <Controller
                    name={fieldName}
                    control={control}
                    defaultValue={defaultValue}
                    render={({field, fieldState}) => (
                        <FormControl
                            required={isRequired}
                            error={!!fieldState.error}
                            component="fieldset"
                            variant="standard"
                            className={clsx(classes.switchLabelWhite, classes.switchLabelGreyDiff)}
                        >
                            <FormControlLabel
                                label={fieldConfig?.label || 'Unknown Field Label'}
                                control={
                                    <Switch
                                        inputRef={field.ref}
                                        checked={!!field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                        onBlur={field.onBlur}
                                    />
                                }
                            />
                            {fieldState.error && <FormHelperText>{fieldState.error?.message}</FormHelperText>}
                        </FormControl>
                    )}
                />
            </Grid>
        );
    };

    /*
     * A row of mutually exclusive choices. Added for the sign-in method on Add New User, where a switch would
     * have been wrong: a toggle says "on or off" and this is a choice between two named paths, neither of which
     * is the absence of the other.
     *
     * Every value goes through String() on the way out and is looked back up in the option list on the way in.
     * That is not cosmetic — radio inputs carry their value as a DOM string, so a source list of booleans would
     * hand the form "false", which is a non-empty string and therefore true everywhere it is later read. The
     * lookup returns the original value from the config, so the form holds a real boolean and the API is sent one.
     */
    const renderRadioInputField = () => {
        if (!['radioInput'].includes(fieldConfig.type)) return <></>;

        const radioOptions = Array.isArray(fieldConfig.options?.source) ? fieldConfig.options.source : [];

        return (
            <Grid item xs={xs} data-testid={data_testid ?? undefined}>
                <Controller
                    name={fieldName}
                    control={control}
                    defaultValue={defaultValue}
                    render={({field, fieldState}) => (
                        <FormControl
                            required={isRequired}
                            error={!!fieldState.error}
                            component="fieldset"
                            variant="standard"
                            className={clsx(classes.switchLabelWhite, classes.switchLabelGreyDiff)}
                        >
                            {/*
                              * Coloured here rather than left to switchLabelWhite, which reaches
                              * `.MuiFormControlLabel-label` — the words beside each button — and never the legend.
                              * On this dark dialog MUI's own default for a FormLabel is text.secondary, which was
                              * dark grey on dark grey: the two choices were legible and the question above them
                              * was not. Mui-focused is restated because MUI turns a focused legend the primary
                              * colour, and error because a legend that is reporting a problem should look like it.
                              */}
                            <FormLabel
                                component="legend"
                                sx={{
                                    color: "#FFFFFF",
                                    fontSize: 14,
                                    mb: 0.5,
                                    "&.Mui-focused": {color: "#FFFFFF"},
                                    "&.Mui-error": {color: "#f44336"},
                                }}
                            >
                                {fieldConfig?.label || 'Unknown Field Label'}
                            </FormLabel>
                            <RadioGroup
                                row={fieldConfig.options?.row ?? true}
                                value={String(field.value)}
                                onChange={(e) => {
                                    const picked = radioOptions.find(option => String(option.value) === e.target.value);
                                    field.onChange(picked !== undefined ? picked.value : e.target.value);
                                }}
                                onBlur={field.onBlur}
                            >
                                {radioOptions.map(option => (
                                    <FormControlLabel
                                        key={String(option.value)}
                                        value={String(option.value)}
                                        control={
                                            <Radio
                                                size="small"
                                                /*
                                                 * The dialog's own gold, not MUI's default primary blue, which
                                                 * is the one colour nothing else on this form uses — the toggle
                                                 * above turns #FFC540 when it is on.
                                                 */
                                                sx={{
                                                    color: "rgba(255,255,255,0.7)",
                                                    "&.Mui-checked": {color: "#FFC540"},
                                                }}
                                            />
                                        }
                                        label={option.label}
                                    />
                                ))}
                            </RadioGroup>
                            {fieldState.error && <FormHelperText>{fieldState.error?.message}</FormHelperText>}
                        </FormControl>
                    )}
                />
            </Grid>
        );
    };

    const unknownTypeFieldRenderer = () => {
        console.error(`Unknown field type: ${fieldConfig?.type} for field: ${fieldConfig?.valueProperty}. Entire field configuration ->`, fieldConfig);
        return <></>
    };

    const fieldRendererMap = {
        defValue: unknownTypeFieldRenderer,
        text: renderTextField,
        dialledNumberInput: renderDialledNumberField,
        multiselect: renderMultiSelectField,
        sizeInput: renderSizeInputField,
        numberInput: renderNumberInputField,
        dateInput: renderDateInputField,
        toggleInput: renderToggleInputField,
        radioInput: renderRadioInputField,
    };

    const hint = fieldConfig.options?.hint;

    return (
        <>
            {(fieldRendererMap[fieldConfig?.type] || fieldRendererMap.defValue)()}
            {hint && (
                <Grid item xs={12}>
                    <Typography sx={{color: "rgba(255,255,255,0.5)", fontSize: 11, fontStyle: "italic", mt: -0.5, ml: 0.5, whiteSpace: "pre-line"}}>
                        *{hint}
                    </Typography>
                </Grid>
            )}
        </>
    );

});